(() => {
  if (window.__PW_CART_DRAWER_CONTROLLER_LOADED__) return;
  window.__PW_CART_DRAWER_CONTROLLER_LOADED__ = true;

  const controllers = new WeakMap();

  class CartDrawerController {
    constructor(root) {
      this.root = root;
      this.config = this.readJSON('[data-pw-cart-drawer-config]', {});
      this.loyalty = this.readJSON('[data-pw-loyalty-config]', {
        freeProducts: [],
        freeCollections: []
      });
      this.cart = null;
      this.cartRevision = 0;
      this.localMutationQueue = Promise.resolve();
      this.destroyed = false;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.discountForm = root.querySelector('.discount-form');
      this.discountInput = root.querySelector('input[name="discount"]');
      this.discountMessage = root.querySelector('.discount-message');
      this.appliedDiscounts = root.querySelector('[data-applied-discounts]');
      this.checkoutMessage = root.querySelector('[data-cart-checkout-message]');
      this.smileContainer = root.querySelector('.smile-loyalty-container');

      window.__freeProductRewards = this.loyalty.freeProducts || [];
      window.__freeCollectionRewards = this.loyalty.freeCollections || [];

      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleCartUpdated = this.handleCartUpdated.bind(this);

      if (this.discountForm) {
        this.discountForm.addEventListener('submit', this.handleSubmit, { signal: this.signal });
      }
      root.addEventListener('click', this.handleClick, { signal: this.signal, capture: true });
      document.addEventListener('pw:cart:updated', this.handleCartUpdated, { signal: this.signal });

      if (this.smileContainer && typeof MutationObserver !== 'undefined') {
        this.smileObserver = new MutationObserver(() => this.updateSmileRewardStates());
        this.smileObserver.observe(this.smileContainer, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['data-code']
        });
      }

      this.loadInitialCart();
    }

    destroy() {
      this.destroyed = true;
      this.abortController.abort();
      if (this.smileObserver) this.smileObserver.disconnect();
      window.clearTimeout(this.messageTimer);
      controllers.delete(this.root);
    }

    readJSON(selector, fallback) {
      const element = this.root.querySelector(selector);
      if (!element) return fallback;
      try {
        return JSON.parse(element.textContent || '');
      } catch (error) {
        console.error('[Cart drawer] Invalid configuration JSON.', error);
        return fallback;
      }
    }

    get api() {
      return window.PWCart || null;
    }

    route(path) {
      if (this.api && typeof this.api.route === 'function') return this.api.route(path);
      const configuredRoot = window.Shopify && window.Shopify.routes
        ? window.Shopify.routes.root
        : '/';
      const root = configuredRoot.endsWith('/') ? configuredRoot : `${configuredRoot}/`;
      return `${root}${String(path).replace(/^\/+/, '')}`;
    }

    requestJSON(url, options) {
      if (this.api && typeof this.api.requestJSON === 'function') {
        return this.api.requestJSON(url, options);
      }

      return fetch(url, { credentials: 'include', ...options }).then(async (response) => {
        const data = await response.json().catch(() => null);
        if (!response.ok) {
          throw new Error((data && (data.description || data.message)) || `Request failed (${response.status})`);
        }
        return data;
      });
    }

    enqueue(task) {
      if (this.api && typeof this.api.enqueue === 'function') return this.api.enqueue(task);
      const run = this.localMutationQueue.then(task, task);
      this.localMutationQueue = run.catch(() => undefined);
      return run;
    }

    sectionNames() {
      const configured = this.api && this.api.sectionNames ? this.api.sectionNames : {};
      return {
        drawer: configured.drawer || this.config.drawerSection || 'cart-drawer',
        main: configured.main || this.config.mainCartSection || 'main-cart'
      };
    }

    sectionPayload() {
      const names = this.sectionNames();
      return {
        sections: [names.drawer, names.main],
        sections_url: window.location.pathname
      };
    }

    async loadInitialCart() {
      const revision = this.cartRevision;
      try {
        const cart = this.api && typeof this.api.fetchCart === 'function'
          ? await this.api.fetchCart()
          : await this.requestJSON(this.route('cart.js'), { cache: 'no-store' });
        if (this.destroyed || revision !== this.cartRevision) return;
        this.updateFromCart(cart);
      } catch (error) {
        if (this.destroyed || revision !== this.cartRevision) return;
        this.renderDiscountPills([]);
        this.showMessage(this.translation('discountValidationError'), 'error');
      }
    }

    handleCartUpdated(event) {
      const cart = event.detail && event.detail.cart;
      if (cart) {
        this.cartRevision += 1;
        this.updateFromCart(cart);
      }
    }

    handleSubmit(event) {
      if (event.target !== this.discountForm) return;
      event.preventDefault();
      const code = this.discountInput ? this.discountInput.value.trim() : '';
      if (!code) {
        this.showMessage(this.translation('discountEnterCode'), 'error');
        return;
      }

      this.setDiscountBusy(true);
      this.enqueue(() => this.applyDiscount(code))
        .catch((error) => {
          console.error('[Cart drawer] Discount apply failed.', error);
          this.showMessage(this.translation('discountValidationError'), 'error');
        })
        .finally(() => this.setDiscountBusy(false));
    }

    handleClick(event) {
      const removeButton = event.target.closest('[data-remove-discount]');
      if (removeButton && this.root.contains(removeButton)) {
        event.preventDefault();
        event.stopPropagation();
        const code = removeButton.dataset.removeDiscount || '';
        if (!code) return;
        this.setDiscountBusy(true);
        this.enqueue(() => this.removeDiscount(code))
          .catch((error) => {
            console.error('[Cart drawer] Discount removal failed.', error);
            this.showMessage(this.translation('discountValidationError'), 'error');
          })
          .finally(() => this.setDiscountBusy(false));
        return;
      }

      const checkout = event.target.closest('[data-cart-checkout-button]');
      if (!checkout || !this.root.contains(checkout)) return;
      this.handleCheckout(event, checkout);
    }

    getApplicableCodes(cart = this.cart) {
      return ((cart && cart.discount_codes) || [])
        .filter((discount) => discount && discount.applicable)
        .map((discount) => discount.code)
        .filter(Boolean);
    }

    getRewardName(code) {
      const map = window.__smileCouponMap || {};
      return map[String(code).toLowerCase()] || '';
    }

    findProductReward(code) {
      const rewardName = this.getRewardName(code).toLowerCase();
      if (!rewardName) return null;
      return (this.loyalty.freeProducts || []).find((reward) => {
        return String(reward.rewardName || '').toLowerCase() === rewardName;
      }) || null;
    }

    findCollectionReward(code) {
      const rewardName = this.getRewardName(code).toLowerCase();
      if (!rewardName) return null;
      return (this.loyalty.freeCollections || []).find((reward) => {
        return String(reward.rewardName || '').toLowerCase() === rewardName;
      }) || null;
    }

    rewardCodeFromItem(item) {
      const properties = item && item.properties ? item.properties : {};
      return String(properties._pw_reward_code || '').toLowerCase();
    }

    findRewardLine(cart, code) {
      const codeLower = String(code).toLowerCase();
      return ((cart && cart.items) || []).find((item) => this.rewardCodeFromItem(item) === codeLower) || null;
    }

    async addRewardLine(code, reward) {
      const payload = {
        items: [{
          id: Number(reward.variantId),
          quantity: 1,
          properties: {
            _pw_reward_code: String(code).toLowerCase(),
            _pw_reward_name: reward.rewardName || ''
          }
        }]
      };

      return this.requestJSON(this.route('cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
    }

    updateDiscountCodes(codes) {
      return this.requestJSON(this.route('cart/update.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          discount: codes.join(','),
          ...this.sectionPayload()
        })
      });
    }

    async removeRewardLine(code, cart, includeSections = true) {
      let currentCart = cart;
      let line = this.findRewardLine(currentCart, code);
      if (!line) {
        currentCart = this.api && typeof this.api.fetchCart === 'function'
          ? await this.api.fetchCart()
          : await this.requestJSON(this.route('cart.js'), { cache: 'no-store' });
        line = this.findRewardLine(currentCart, code);
      }
      if (!line) return currentCart;

      const payload = { id: line.key, quantity: 0 };
      if (includeSections) Object.assign(payload, this.sectionPayload());
      return this.requestJSON(this.route('cart/change.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
    }

    async applyDiscount(code) {
      const codeLower = code.toLowerCase();
      let initialCart = this.cart;
      if (!initialCart) {
        initialCart = this.api && typeof this.api.fetchCart === 'function'
          ? await this.api.fetchCart()
          : await this.requestJSON(this.route('cart.js'), { cache: 'no-store' });
      }

      const existingCodes = this.getApplicableCodes(initialCart);
      if (existingCodes.some((existing) => existing.toLowerCase() === codeLower)) {
        if (this.discountInput) this.discountInput.value = '';
        return initialCart;
      }

      const productReward = this.findProductReward(code);
      const collectionReward = this.findCollectionReward(code);
      let rewardLineExists = Boolean(this.findRewardLine(initialCart, code));
      let rewardLineAdded = false;

      try {
        if (productReward && productReward.variantId && !rewardLineExists) {
          await this.addRewardLine(code, productReward);
          rewardLineAdded = true;
          rewardLineExists = true;
        }

        const attemptedCodes = [...existingCodes, code];
        const attemptedCart = await this.updateDiscountCodes(attemptedCodes);
        const returnedCodes = attemptedCart.discount_codes || [];
        const newCode = returnedCodes.find((discount) => {
          return String(discount.code || '').toLowerCase() === codeLower;
        });
        const newCodeApplied = Boolean(newCode && newCode.applicable);
        const existingStillApply = existingCodes.every((existingCode) => {
          const existingLower = existingCode.toLowerCase();
          const returned = returnedCodes.find((discount) => {
            return String(discount.code || '').toLowerCase() === existingLower;
          });
          return Boolean(returned && returned.applicable);
        });

        if (newCodeApplied && existingStillApply) {
          if (this.discountInput) this.discountInput.value = '';
          this.renderCartResponse(attemptedCart, 'discount');
          this.showMessage(
            this.translation('discountApplied').replace('__CODE__', code),
            'success'
          );
          return attemptedCart;
        }

        let revertedCart = await this.updateDiscountCodes(existingCodes);
        if (rewardLineExists) revertedCart = await this.removeRewardLine(code, revertedCart, true);
        this.renderCartResponse(revertedCart, 'discount-reverted');

        if (!existingStillApply) {
          this.showMessage(
            this.translation('discountConflict')
              .replace('__NEW__', code)
              .replace('__OLD__', existingCodes.join(', ')),
            'error'
          );
        } else if (collectionReward) {
          this.showMessage(
            this.translation('collectionAddProduct')
              .replace('__COLLECTION__', collectionReward.collectionTitle || ''),
            'error'
          );
        } else {
          this.showMessage(this.translation('discountInvalid'), 'error');
        }

        return revertedCart;
      } catch (error) {
        if (rewardLineAdded) {
          try {
            const cleanedCart = await this.removeRewardLine(code, null, true);
            this.renderCartResponse(cleanedCart, 'reward-cleanup');
          } catch (cleanupError) {
            console.error('[Cart drawer] Reward cleanup failed.', cleanupError);
          }
        }
        throw error;
      }
    }

    async removeDiscount(code) {
      const originalCart = this.cart || await (
        this.api && typeof this.api.fetchCart === 'function'
          ? this.api.fetchCart()
          : this.requestJSON(this.route('cart.js'), { cache: 'no-store' })
      );
      const originalCodes = this.getApplicableCodes(originalCart);
      const codeLower = code.toLowerCase();
      const remainingCodes = originalCodes.filter((existing) => existing.toLowerCase() !== codeLower);
      let updatedCart = await this.updateDiscountCodes(remainingCodes);

      try {
        if (this.findRewardLine(updatedCart, code)) {
          updatedCart = await this.removeRewardLine(code, updatedCart, true);
        }
      } catch (error) {
        const rollbackCart = await this.updateDiscountCodes(originalCodes);
        this.renderCartResponse(rollbackCart, 'discount-remove-rollback');
        throw error;
      }

      this.renderCartResponse(updatedCart, 'discount-remove');
      this.showMessage(this.translation('discountRemoved'), 'success');
      return updatedCart;
    }

    renderCartResponse(cart, source) {
      if (!cart) return;
      this.cartRevision += 1;
      if (this.api && typeof this.api.renderSections === 'function') {
        this.api.renderSections(cart.sections, cart);
      }
      this.updateFromCart(cart);
      if (this.api && typeof this.api.emitUpdated === 'function') {
        this.api.emitUpdated(cart, source);
      }
    }

    updateFromCart(cart) {
      if (!cart || this.destroyed) return;
      this.cart = cart;
      this.updateFreeShipping(cart);
      this.updateCheckoutState(cart);
      this.renderDiscountPills(cart.discount_codes || []);
      this.updateSmileRewardStates();
    }

    updateFreeShipping(cart) {
      const notice = this.root.querySelector('[data-free-shipping-threshold]');
      if (!notice) return;
      const threshold = Number.parseInt(notice.dataset.freeShippingThreshold || '0', 10);
      if (!Number.isFinite(threshold) || threshold <= 0) return;

      const total = Number(cart.total_price) || 0;
      const count = Number(cart.item_count) || 0;
      const remaining = threshold - total;
      const text = notice.querySelector('.cart-notice__text');
      const bar = notice.querySelector('.progress-bar');
      notice.classList.toggle('is-hidden', count === 0);

      const nextColor = remaining <= 0
        ? notice.dataset.progressColorComplete
        : notice.dataset.progressColorIncomplete;
      if (nextColor) notice.style.setProperty('--pw-progress-bar-color', nextColor);

      if (text) {
        if (remaining <= 0) {
          const eligible = notice.dataset.textEligible || '';
          if (text.textContent !== eligible) text.textContent = eligible;
        } else {
          const template = notice.dataset.textRemainingTemplate || '__AMOUNT__';
          const amount = this.formatAmount(remaining);
          const html = template.replace('__AMOUNT__', amount);
          if (text.innerHTML !== html) text.innerHTML = html;
        }
      }

      if (bar) {
        const progress = Math.max(0, Math.min(100, (total / threshold) * 100));
        bar.style.width = `${progress.toFixed(2)}%`;
      }
    }

    formatAmount(cents) {
      const locale = this.config.locale || 'nl-NL';
      try {
        return new Intl.NumberFormat(locale, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(Number(cents) / 100);
      } catch (error) {
        return (Number(cents) / 100).toFixed(2).replace('.', ',');
      }
    }

    updateCheckoutState(cart) {
      const checkout = this.root.querySelector('[data-cart-checkout-button]');
      if (!checkout) return;
      const disabled = !cart || Number(cart.item_count) <= 0;
      checkout.classList.toggle('disabled', disabled);
      if (disabled) checkout.setAttribute('aria-disabled', 'true');
      else checkout.removeAttribute('aria-disabled');
    }

    renderDiscountPills(discounts) {
      if (!this.appliedDiscounts) return;
      const applicable = (discounts || []).filter((discount) => discount && discount.applicable);
      const discountBlock = this.appliedDiscounts.closest('.cart__discount');
      if (discountBlock) {
        discountBlock.classList.toggle('has-applied-discounts', applicable.length > 0);
      }
      const fragment = document.createDocumentFragment();

      applicable.forEach((discount) => {
        const row = document.createElement('div');
        row.className = 'discount-applied';
        row.dataset.discountCode = discount.code;

        const text = document.createElement('span');
        text.className = 'discount-code-text';
        text.textContent = discount.code;

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'discount-remove-btn';
        remove.dataset.removeDiscount = discount.code;
        remove.textContent = this.translation('removeDiscount');
        remove.setAttribute('aria-label', `${this.translation('removeDiscount')}: ${discount.code}`);

        row.append(text, remove);
        fragment.append(row);
      });

      this.appliedDiscounts.replaceChildren(fragment);
    }

    updateSmileRewardStates() {
      const appliedCodes = new Set(this.getApplicableCodes().map((code) => code.toLowerCase()));
      this.root.querySelectorAll('.smile-loyalty__apply-btn[data-code]').forEach((button) => {
        const applied = appliedCodes.has(String(button.dataset.code || '').toLowerCase());
        const reward = button.closest('.smile-loyalty__reward');

        if (applied) {
          if (!button.dataset.pwDiscountApplied) {
            button.dataset.pwWasDisabled = button.disabled ? 'true' : 'false';
          }
          button.dataset.pwDiscountApplied = 'true';
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
          button.classList.add('is-applied');
          if (reward) reward.classList.add('is-applied');
        } else if (button.dataset.pwDiscountApplied === 'true') {
          button.disabled = button.dataset.pwWasDisabled === 'true';
          if (!button.disabled) button.removeAttribute('aria-disabled');
          button.classList.remove('is-applied');
          delete button.dataset.pwDiscountApplied;
          delete button.dataset.pwWasDisabled;
          if (reward) reward.classList.remove('is-applied');
        }
      });
    }

    setDiscountBusy(busy) {
      if (!this.discountForm) return;
      const button = this.discountForm.querySelector('button[type="submit"]');
      if (button) {
        if (busy) {
          button.dataset.originalText = button.textContent;
          button.textContent = this.translation('discountValidating');
        } else if (button.dataset.originalText) {
          button.textContent = button.dataset.originalText;
          delete button.dataset.originalText;
        }
        button.disabled = busy;
      }
      if (this.discountInput) this.discountInput.disabled = busy;
      this.discountForm.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    showMessage(message, type) {
      if (!this.discountMessage || !message) return;
      this.discountMessage.textContent = message;
      this.discountMessage.className = `discount-message discount-message--${type}`;
      window.clearTimeout(this.messageTimer);
      this.messageTimer = window.setTimeout(() => {
        if (!this.discountMessage) return;
        this.discountMessage.textContent = '';
        this.discountMessage.className = 'discount-message';
      }, type === 'error' ? 6000 : 3000);
    }

    translation(key) {
      const translations = this.config.translations || {};
      const fallbacks = {
        discountApplied: 'Kortingscode __CODE__ is toegepast.',
        discountRemoved: 'De kortingscode is verwijderd.',
        discountInvalid: 'Deze kortingscode is niet geldig voor deze winkelwagen.',
        discountValidationError: 'De kortingscode kon niet worden verwerkt. Probeer het opnieuw.',
        discountEnterCode: 'Vul eerst een kortingscode in.',
        discountValidating: 'Controleren…',
        discountConflict: 'Kortingscode __NEW__ kan niet samen met __OLD__ worden gebruikt.',
        collectionAddProduct: 'Voeg eerst een product uit __COLLECTION__ toe.',
        removeDiscount: 'Verwijderen',
        termsRequired: 'Selecteer alstublieft de algemene voorwaarden.'
      };
      return translations[key] || fallbacks[key] || '';
    }

    handleCheckout(event, button) {
      event.preventDefault();
      if (button.classList.contains('disabled') || button.getAttribute('aria-disabled') === 'true') return;

      const terms = this.root.querySelector('input[name="terms"], #rterms-0')
        || document.getElementById('rterms-0');
      if (terms && !terms.checked) {
        if (this.checkoutMessage) this.checkoutMessage.textContent = this.translation('termsRequired');
        terms.focus();
        return;
      }

      if (this.checkoutMessage) this.checkoutMessage.textContent = '';
      button.style.pointerEvents = 'none';
      button.setAttribute('aria-busy', 'true');

      const noteField = this.root.querySelector('textarea[name="note"]');
      const noteElement = noteField ? noteField.closest('cart-note') : null;
      const save = noteElement && typeof noteElement.flushPendingSave === 'function'
        ? noteElement.flushPendingSave()
        : Promise.resolve();
      const timeout = new Promise((resolve) => window.setTimeout(resolve, 4000));

      Promise.race([Promise.resolve(save).catch(() => undefined), timeout])
        .finally(() => {
          window.location.assign(this.config.checkoutUrl || '/checkout');
        });
    }
  }

  const initRoot = (root) => {
    if (!root || controllers.has(root)) return;
    controllers.set(root, new CartDrawerController(root));
  };

  const initAll = (scope = document) => {
    if (scope.matches && scope.matches('[data-pw-cart-drawer]')) initRoot(scope);
    scope.querySelectorAll?.('[data-pw-cart-drawer]').forEach(initRoot);
  };

  window.PWCartDrawer = Object.assign(window.PWCartDrawer || {}, {
    init: initAll
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('pw:cart-drawer:rendered', (event) => {
    const root = event.detail && event.detail.root;
    initAll(root || document);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    const roots = [];
    if (event.target.matches?.('[data-pw-cart-drawer]')) roots.push(event.target);
    event.target.querySelectorAll?.('[data-pw-cart-drawer]').forEach((root) => roots.push(root));
    roots.forEach((root) => {
      const controller = controllers.get(root);
      if (controller) controller.destroy();
    });
  });
})();
